// ================================================================
// vm-bottom-sheet
//
// Mobile bottom sheet overlay with drag-to-dismiss. Renders a
// backdrop + sheet that slides up from the bottom edge.
//
// BEM block: bottom-sheet
// ================================================================

@use "Styles/mixins" as mixins;

// -- Host --------------------------------------------------------

:host {
    display: contents;
}

:host(:not([open])) {
    display: none;
}

// -- Block -------------------------------------------------------

.bottom-sheet {
    // -- Overlay -------------------------------------------------

    &__overlay {
        position: fixed;
        inset: 0;
        z-index: mixins.get-z(modal, dialog);
        display: flex;
        align-items: flex-end;

        &--closing {
            pointer-events: none;
        }

        :host([disable-backdrop]) & {
            pointer-events: none;
        }
    }

    // -- Backdrop ------------------------------------------------

    &__backdrop {
        position: absolute;
        inset: 0;
        @include mixins.overlay-backdrop;

        .bottom-sheet__overlay--closing & {
            @include mixins.overlay-backdrop-closing;
        }
    }

    // -- Sheet ---------------------------------------------------

    &__sheet {
        position: relative;
        width: 100%;
        max-height: 100vh;
        background-color: mixins.get-color(background, bg-white);
        border-radius: 24px 24px 0 0;
        box-shadow:
            -2px -12px 48px -12px rgba(10, 13, 18, 0.18),
            4px -8px 4px -2px rgba(10, 13, 18, 0.04);
        display: flex;
        flex-direction: column;
        transition: height 300ms cubic-bezier(0.16, 1, 0.3, 1);
        animation: bottom-sheet-slide-up 300ms cubic-bezier(0.16, 1, 0.3, 1);

        &--closing {
            animation: bottom-sheet-slide-down 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        :host([disable-backdrop]) & {
            pointer-events: auto;
        }
    }

    // -- Header (doubles as drag target) -------------------------

    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        touch-action: none;
        font-size: mixins.get-font-size(text-md);
        font-style: normal;
        font-weight: 700;
        line-height: mixins.get-line-height(text-md);

        @media (hover: none) and (pointer: coarse) {
            cursor: grab;
        }
    }

    &__drag-handle {
        width: 36px;
        height: 5px;
        margin: 5px 0 6px;
        border-radius: 100px;
        background-color: mixins.get-color(border, border-primary);
    }

    &__header-row {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        box-sizing: border-box;
        width: 100%;
        padding: 0 16px;
        height: 44px;
    }

    &__header-title {
        grid-column: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
        overflow: hidden;

        ::slotted(*) {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    // -- Close button --------------------------------------------

    &__close-btn {
        grid-column: 3;
        justify-self: end;
        appearance: none;
        background: none;
        border: none;
        margin: 0;
        padding: 0;
        flex-shrink: 0;
        color: mixins.get-color(text, text-primary);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    // -- Body ----------------------------------------------------

    &__body {
        flex: 1 1 0%;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        min-height: 0;
        padding-bottom: env(safe-area-inset-bottom, 0);

        slot {
            display: block;
            padding: var(--vm-bottom-sheet-body-padding, 8px 16px);
        }
    }
}

// ================================================================
// Utilities
// ================================================================

.bottom-sheet__visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

// ================================================================
// Animations
// ================================================================

@keyframes bottom-sheet-slide-up {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
}

@keyframes bottom-sheet-slide-down {
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(100%);
    }
}
